import React, { useState, useEffect, useCallback } from "react";
import "./PersonaCreator.css";
import Modal from "../shared/Modal";
import { useSelector } from "react-redux";
import { selectHrisConnected, selectUser } from "../../redux/features/appData";
import {
  apiCreatePersona,
  apiUpdatePersona,
  apiDeletePersona,
  apiEvaluatePersonaCondition,
} from "../../services/PersonasService";
import { Employee } from "winslow-core";
import { ConditionCard } from "./ConditionCard";
import { PersonaCondition, PersonaExpanded, RuleType } from "winslow-core";
import { Button } from "../../ds2/components";
import { useEmployeeCache } from "../../hooks/useEmployeeCache";

import { toastFail } from "../../config";
import toast from "react-hot-toast";
import { DeleteModal } from "./PersonaDeleteModal";
import { ModalMatchingEmployees } from "./ModalMatchingEmployees";
import { debounce } from "lodash";
import { ToggleSwitch } from "../common/ToggleSwitch";
import { MyTooltip } from "../common/MyTooltip";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "lucide-react";

export interface InputFieldProps {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder: string;
}

export const InputField: React.FC<InputFieldProps> = ({
  value,
  onChange,
  maxLength,
  placeholder,
}) => {
  return (
    <div className="persona-creator-input-container">
      <label htmlFor="personaInput" className="persona-creator-visually-hidden">
        {placeholder}
      </label>
      <input
        id="personaInput"
        className="persona-creator-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
      />
    </div>
  );
};

interface PersonaCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  existingPersona?: PersonaExpanded | null;
  onPersonaSaved: () => void;
}

export const PersonaCreator: React.FC<PersonaCreatorProps> = ({
  isOpen,
  onClose,
  existingPersona,
  onPersonaSaved,
}) => {
  const [personaName, setPersonaName] = useState("");
  const [personaDescription, setPersonaDescription] = useState("");
  const [conditions, setConditions] = useState<PersonaCondition[]>([]);
  const [defaultPersona, setDefaultPersona] = useState(false);
  const storeUser = useSelector(selectUser);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [matchingEmployees, setMatchingEmployees] = useState<Employee[]>([]);
  const [showMatchingEmployees, setShowMatchingEmployees] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const hrisConnected = useSelector(selectHrisConnected);
  const { employees } = useEmployeeCache();

  const navigate = useNavigate();

  const debouncedEvaluateConditions = useCallback(
    debounce(async (conditions: PersonaCondition[], workspaceId: string) => {
      setIsEvaluating(true);
      try {
        const employeeProfilesResult = await apiEvaluatePersonaCondition({
          conditions,
          workspaceId,
        });

        if (employeeProfilesResult) {
          setMatchingEmployees(employeeProfilesResult);
        }
      } finally {
        setIsEvaluating(false);
      }
    }, 1000),
    []
  );

  useEffect(() => {
    if (conditions.length === 0) {
      setMatchingEmployees([]);
      return;
    }

    if (storeUser?.currentWorkspaceId) {
      debouncedEvaluateConditions(conditions, storeUser.currentWorkspaceId);
    }

    return () => {
      debouncedEvaluateConditions.cancel();
    };
  }, [conditions, storeUser?.currentWorkspaceId, debouncedEvaluateConditions]);

  useEffect(() => {
    if (!isOpen) {
      setPersonaName("");
      setPersonaDescription("");
      setConditions([]);
      setDefaultPersona(false);
      setShowDeleteModal(false);
    } else if (existingPersona) {
      setPersonaName(existingPersona.name);
      setPersonaDescription(existingPersona.description || "");
      setConditions(existingPersona.logic.conditions);
      setDefaultPersona(existingPersona.default || false);
    } else {
      setPersonaName("");
      setPersonaDescription("");
      setConditions([]);
      setDefaultPersona(false);
    }
  }, [isOpen, existingPersona]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  const hasIncompleteCondition = (conditions: PersonaCondition[]): boolean => {
    return conditions.some(
      (condition) =>
        !condition.attributePath || !condition.rule || !condition.value
    );
  };

  const handleAddCondition = () => {
    setConditions((prev) => [
      ...prev,
      {
        attributePath: "",
        rule: "" as RuleType,
        value: "",
      },
    ]);
  };

  const handleDeleteCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => {
      console.log("condition filtering", {
        index,
        i,
      });
      return i !== index;
    });
    console.log("newConditions", { index, newConditions });
    setConditions(newConditions);
  };

  const handleConditionChange = (
    condition: PersonaCondition,
    index: number
  ) => {
    setConditions((prev) => {
      const newConditions = [...prev];
      newConditions[index] = condition;
      return newConditions;
    });
  };

  const handleSavePersona = async () => {
    if (!storeUser?.currentWorkspaceId || !storeUser?.id) return;

    try {
      if (existingPersona && storeUser.currentWorkspaceId) {
        await apiUpdatePersona({
          id: existingPersona.id,
          name: personaName,
          logic: {
            conditions: conditions,
          },
          description: personaDescription,
          default: defaultPersona,
        });
      } else {
        await apiCreatePersona({
          workspaceId: storeUser.currentWorkspaceId,
          userId: storeUser.id,
          name: personaName,
          description: personaDescription,
          logic: {
            conditions: conditions,
          },
          default: defaultPersona,
        });
      }

      onPersonaSaved();
      onClose();
    } catch (error) {
      console.error("Error saving persona:", error);
      toast("Error saving persona", toastFail);
    }
  };

  const handleDeletePersona = async () => {
    if (!existingPersona || !storeUser?.currentWorkspaceId) return;

    if (
      existingPersona.personaLinkRecords &&
      existingPersona.personaLinkRecords.length > 0
    ) {
      setShowDeleteModal(true);
      return;
    }

    await performDelete();
  };

  const performDelete = async () => {
    if (!existingPersona) return;

    try {
      await apiDeletePersona(existingPersona.id);
      onPersonaSaved();
      onClose();
    } catch (error) {
      console.error("Error deleting persona:", error);
      toast("Error deleting persona", toastFail);
    }
  };

  const isCreateButtonDisabled = () => {
    return (
      !personaName.trim() || // Name requirement
      hasIncompleteCondition(conditions) // All conditions must be complete
    );
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      style={{ width: "600px" }}
      canClose={true}
      escapeToClose={true}
      allowClickout={true}>
      <div className="persona-creator-modal-container">
        <header className="persona-creator-header">
          <h1 className="persona-creator-title">
            {existingPersona ? "Edit Persona" : "Create Persona"}
          </h1>

          {!hrisConnected && (
            <Button
              variant="primary"
              onClick={() => navigate("/waz/settings/workspaces?tab=4")}
            >
              Connect HRIS
            </Button>
          )}

          {/* {hrisConnected && (
            <Button
              variant="primary"
              onClick={() => navigate("/waz/settings/workspaces?tab=4")}
            >
              Connect HRIS
            </Button>
          )} */}
            {/* <div
              className={`persona-creator-matching-employees-count clickable`}
              onClick={() => navigate("/waz/settings/workspaces?tab=4")}>
              Connect HRIS
            </div>
          )} */}

          {hrisConnected && (
            <>
             <Button
                disabled={matchingEmployees.length < 1}
                onClick={
                  matchingEmployees.length > 0
                    ? () => setShowMatchingEmployees(true)
                    : undefined
                }
              >
                {isEvaluating ? (
                  <>
                    <div className="persona-creator-spinner"></div>
                    Evaluating...
                  </>
                ) : matchingEmployees.length > 0 ? (
                  `${matchingEmployees.length} Matches`
                ) : (
                  "No Matches"
                )}
              </Button>

            {/* <div
              className={`persona-creator-matching-employees-count ${
                matchingEmployees.length > 0 ? "clickable" : ""
              }`}
              onClick={
                matchingEmployees.length > 0
                  ? () => setShowMatchingEmployees(true)
                  : undefined
              }>
              {isEvaluating ? (
                <>
                  <div className="persona-creator-spinner"></div>
                  Evaluating...
                </>
              ) : matchingEmployees.length > 0 ? (
                `${matchingEmployees.length} Matching Employees`
              ) : (
                "No Matching Employees"
              )}
            </div> */}
            </>
          )}
        </header>

        <div className="persona-creator-form">
          <InputField
            value={personaName}
            onChange={setPersonaName}
            maxLength={100}
            placeholder="Name your persona..."
          />

          <InputField
            value={personaDescription}
            onChange={setPersonaDescription}
            maxLength={200}
            placeholder="Describe your persona..."
          />

          <section className="persona-creator-conditions-section">
            <MyTooltip title="Default personas will be added to all new resources.">
              <div className="persona-creator-default-persona-toggle">
                <ToggleSwitch
                  checked={defaultPersona}
                  onChange={() => setDefaultPersona(!defaultPersona)}
                  label="Default"
                />
                Default{" "}
              </div>
            </MyTooltip>
            <div className="persona-creator-conditions-header">
              <h2 className="persona-creator-conditions-title">Conditions</h2>
              

              <Button
                // className="persona-creator-button-primary"
                onClick={handleAddCondition}
                disabled={hasIncompleteCondition(conditions)}
                leftIcon={<PlusIcon size="16"/>}
              >
                Condition
              </Button>
            </div>

            <div className="persona-creator-conditions-content">
              {conditions.length === 0 ? (
                <div className="persona-creator-placeholder-content">
                  <span className="persona-creator-placeholder-title">
                    Add a new condition
                  </span>
                  <p className="persona-creator-placeholder-text">
                    Click on the + button to add a new condition.
                  </p>
                </div>
              ) : (
                <div className="persona-creator-conditions-wrapper">
                  <div className="persona-creator-conditions-scroll">
                    {conditions.map((condition, index) => (
                      <React.Fragment key={`condition-fragment-${index}`}>
                        {index > 0 && (
                          <hr className="persona-creator-condition-divider" />
                        )}
                        <ConditionCard
                          index={index}
                          key={`condition-card-${index}`}
                          condition={condition}
                          employees={employees}
                          onDelete={handleDeleteCondition}
                          onChange={(updatedCondition) =>
                            handleConditionChange(updatedCondition, index)
                          }
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <footer className="persona-creator-actions">
            {existingPersona && 
              <div style={{ marginRight: "auto" }}>
                <Button
                  // className="persona-creator-button-delete"
                  onClick={handleDeletePersona}
                  // type="button"
                  
                >
                  Delete
                </Button>
              </div>
            }
            <Button
              variant="secondary"
              // className="persona-creator-button-secondary"
              onClick={handleClose}
              // type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSavePersona}
              disabled={isCreateButtonDisabled()}
            >
              {existingPersona ? "Save" : "Create"}
            </Button>
          </footer>
        </div>
      </div>
      {showDeleteModal && (
        <Modal
          isOpen={true}
          onClose={() => handleClose()}
          style={{ width: "512px" }}
          canClose={true}
          escapeToClose={true}
          allowClickout={true}>
          <DeleteModal
            onCancel={() => setShowDeleteModal(false)}
            onDelete={async () => {
              setShowDeleteModal(false);
              await performDelete();
            }}
          />
        </Modal>
      )}
      {showMatchingEmployees && (
        <ModalMatchingEmployees
          isOpen={showMatchingEmployees}
          onClose={() => setShowMatchingEmployees(false)}
          employees={matchingEmployees}
        />
      )}
    </Modal>
  );
};
